import axios from 'axios';

import type {
  BillingContact,
  BillingMode,
  Customer,
  Licence,
  PersistedStripePrice,
  Subscription,
  TrialData,
} from '@sine/sine-core-api-types';

import type { BillingSite, Card, Currency } from './billing.types';

const BILLING_ENDPOINT = `${window.env.REACT_APP_SINE_CORE_API_URI}/billing`;

const billingService = {
  async listSubscriptions(teamId: string) {
    const { data } = await axios.get<Array<Subscription>>(`${BILLING_ENDPOINT}/team/${teamId}/subscriptions`);
    return data;
  },

  async getCustomer(teamId: string): Promise<Customer | undefined> {
    const { data } = await axios.get<Customer>(`${BILLING_ENDPOINT}/team/${teamId}/customer`);
    return data;
  },

  async listPrices(teamId: string, customerCurrency: Currency) {
    const { data } = await axios.get<Array<PersistedStripePrice>>(
      `${BILLING_ENDPOINT}/team/${teamId}/prices?currency=${customerCurrency}`,
    );
    return data;
  },

  async listSites(teamId: string) {
    const { data } = await axios.get<Array<BillingSite>>(`${BILLING_ENDPOINT}/team/${teamId}/sites`);
    return data;
  },

  async createSubscription(params: Record<string, any>) {
    const { data } = await axios.post<Subscription>(`${BILLING_ENDPOINT}/subscription`, params);
    return data;
  },

  async createManualLicence(params: Record<string, any>) {
    const { data } = await axios.post<Licence>(`${BILLING_ENDPOINT}/licence/manual`, params);
    return data;
  },

  async createManualSubscription(params: Record<string, any>) {
    const { data } = await axios.post<Subscription>(`${BILLING_ENDPOINT}/subscription/manual`, params);
    return data;
  },

  async cancelSubscription(teamId: string, subscriptionId: string) {
    const { data } = await axios.post<Subscription>(`${BILLING_ENDPOINT}/subscription/cancel`, {
      team: teamId,
      subscription: subscriptionId,
    });
    return data;
  },

  async cancelLicence(teamId: string, licenceId: string) {
    const { data } = await axios.post<Licence>(`${BILLING_ENDPOINT}/licence/cancel`, {
      team: teamId,
      licence: licenceId,
    });
    return data;
  },

  async assignLicence(teamId: string, licenceId: string, siteId: string) {
    const { data } = await axios.post<Licence>(`${BILLING_ENDPOINT}/assign-licence-to-site`, {
      team: teamId,
      licence: licenceId,
      site: siteId,
    });
    return data;
  },

  async deleteLicence(licenceId: string) {
    const { data } = await axios.delete<Licence>(`${BILLING_ENDPOINT}/licence/manual/${licenceId}`);
    return data;
  },

  async releaseLicence(teamId: string, licenceId: string) {
    const { data } = await axios.post<Licence>(`${BILLING_ENDPOINT}/release-licence`, {
      team: teamId,
      licence: licenceId,
    });
    return data;
  },

  async updateCards(teamId: string, cards: Array<Pick<Card, 'id' | 'default'>>) {
    const { data } = await axios.post<Array<Card>>(`${BILLING_ENDPOINT}/update-cards`, {
      team: teamId,
      cards,
    });
    return data;
  },

  async updateLicenceNote(licenceId: string, licenceNote: string) {
    const { data } = await axios.put<Licence>(`${BILLING_ENDPOINT}/licence/${licenceId}/note`, {
      licenceId,
      licenceNote,
    });
    return data;
  },

  async getTrialData() {
    const { data } = await axios.get<TrialData>(`${BILLING_ENDPOINT}/trial-data`);
    return data;
  },

  async updateBillingMode(teamId: string, params: Record<string, any>) {
    const { data } = await axios.put<Pick<BillingMode, 'mode' | 'team'>>(
      `${BILLING_ENDPOINT}/team/${teamId}/mode`,
      params,
    );
    return data;
  },

  async createOrUpdateBillingContact(teamId: string, params: Record<string, any>) {
    const { data } = await axios.put<BillingContact>(`${BILLING_ENDPOINT}/team/${teamId}/contact`, params);
    return data;
  },

  async getBillingMode(teamId: string) {
    const { data } = await axios.get<Pick<BillingMode, 'mode' | 'team'>>(`${BILLING_ENDPOINT}/team/${teamId}/mode`);
    return data.mode;
  },

  async getBillingContact(teamId: string) {
    const { data } = await axios.get<BillingContact>(`${BILLING_ENDPOINT}/team/${teamId}/contact`);
    return data;
  },
};

export default billingService;
